<template>
    <div class="flex flex-column xl:flex-row gap-2 my-2">
        <Card class="flex-1 chart-card" style="min-height: 400px;">
            <template #title>
                <div class="flex flex-wrap justify-content-between align-items-start gap-2">
                    <div>
                        <h4 class="m-0">Média diária de cadastros de boleto por colaborador</h4>
                        <small class="text-color-secondary font-normal">por data de criação do cadastro</small>
                    </div>
                    <div class="flex flex-wrap align-items-center gap-2 w-full md:w-auto">
                        <MultiSelect
                            v-model="mediaColaboradoresVisiveis"
                            :options="mediaColaboradoresOpcoes"
                            optionLabel="label"
                            optionValue="value"
                            placeholder="Colaboradores"
                            display="comma"
                            :maxSelectedLabels="1"
                            selectedItemsLabel="{0} selecionados"
                            filter
                            scrollHeight="240px"
                            class="w-full md:w-16rem colaborador-multiselect"
                        />
                        <div class="flex align-items-center gap-1">
                            <Button type="button" label="Top 5" size="small" severity="secondary" outlined @click="selecionarMediaTop5" />
                            <Button type="button" label="Todos" size="small" severity="secondary" outlined @click="selecionarMediaTodos" />
                            <Button type="button" label="Limpar" size="small" severity="secondary" outlined @click="limparSelecaoMedia" />
                        </div>
                    </div>
                </div>
            </template>
            <template #content>
                <div class="chart-area">
                    <Chart v-if="mediaChart.labels.length" type="bar" :data="mediaChart" :options="mediaOptions" />
                    <div v-else-if="!loading && (media.labels || []).length" class="text-center p-4">Selecione ao menos um colaborador.</div>
                    <div v-else-if="!loading" class="text-center p-4">Sem dados no período.</div>
                    <div v-if="loading" class="chart-loader">
                        <ProgressSpinner style="width: 40px; height: 40px" strokeWidth="6" />
                        <span>CARREGANDO GRÁFICO...</span>
                    </div>
                </div>
            </template>
        </Card>

        <Card class="flex-1 chart-card" style="min-height: 400px;">
            <template #title>
                <div class="flex flex-wrap justify-content-between align-items-start gap-2">
                    <div>
                        <h4 class="m-0">Evolução diária de cadastros de boleto por colaborador</h4>
                        <small class="text-color-secondary font-normal">por data de criação do cadastro</small>
                    </div>
                    <div class="flex flex-wrap align-items-center gap-2 w-full md:w-auto">
                        <MultiSelect
                            v-model="colaboradoresVisiveis"
                            :options="colaboradoresOpcoes"
                            optionLabel="label"
                            optionValue="value"
                            placeholder="Colaboradores"
                            display="comma"
                            :maxSelectedLabels="1"
                            selectedItemsLabel="{0} selecionados"
                            filter
                            scrollHeight="240px"
                            class="w-full md:w-16rem colaborador-multiselect"
                        />
                        <div class="flex align-items-center gap-1">
                            <Button type="button" label="Top 5" size="small" severity="secondary" outlined @click="selecionarTop5" />
                            <Button type="button" label="Todos" size="small" severity="secondary" outlined @click="selecionarTodos" />
                            <Button type="button" label="Limpar" size="small" severity="secondary" outlined @click="limparSelecao" />
                        </div>
                    </div>
                </div>
            </template>
            <template #content>
                <div class="chart-area">
                    <Chart v-if="evolucaoChart.datasets.length" type="line" :data="evolucaoChart" :options="evolucaoOptions" />
                    <div v-else-if="!loading && evolucao.datasets.length" class="text-center p-4">Selecione ao menos um colaborador.</div>
                    <div v-else-if="!loading" class="text-center p-4">Sem dados no período.</div>
                    <div v-if="loading" class="chart-loader">
                        <ProgressSpinner style="width: 40px; height: 40px" strokeWidth="6" />
                        <span>CARREGANDO GRÁFICO...</span>
                    </div>
                </div>
            </template>
        </Card>
    </div>
</template>

<script>
import MultiSelect from 'primevue/multiselect';
import Button from 'primevue/button';
import Card from 'primevue/card';
import ProgressSpinner from 'primevue/progressspinner';
import SafeChart from '@/components/SafeChart.vue';
import Conta from '@/service/conta.js';

const CORES = ['#3B82F6', '#EF4444', '#22C55E', '#F59E0B', '#14B8A6', '#EC4899', '#8B5CF6', '#64748B', '#F97316', '#84CC16'];
const MAX_MEDIA_BARS = 15;

const nomeCurto = (nome) => String(nome).trim().split(/\s+/).slice(0, 2).join(' ');

const rotuloDia = (iso) => iso.slice(8, 10) + '/' + iso.slice(5, 7);

export default {
    components: { MultiSelect, Button, Card, ProgressSpinner, Chart: SafeChart },
    props: {
        query: {
            type: String,
            default: '',
        },
    },
    data() {
        return {
            loading: false,
            reqId: 0,
            media: { labels: [], data: [] },
            evolucao: { labels: [], datasets: [] },
            mediaColaboradoresVisiveis: [],
            colaboradoresVisiveis: [],
            mediaOptions: {
                responsive: true,
                plugins: {
                    legend: { display: false },
                    tooltip: {
                        callbacks: {
                            title: (items) => items[0]?.label || '',
                            label: (ctx) => `Média diária: ${ctx.parsed.y.toLocaleString('pt-BR', { maximumFractionDigits: 2 })}`,
                        },
                    },
                },
                scales: {
                    y: { beginAtZero: true, title: { display: true, text: 'Média diária' } },
                },
            },
            evolucaoOptions: {
                responsive: true,
                interaction: { mode: 'index', intersect: false },
                plugins: { legend: { position: 'top' }, tooltip: { itemSort: (a, b) => b.parsed.y - a.parsed.y } },
                scales: {
                    y: { beginAtZero: true, ticks: { precision: 0 }, title: { display: true, text: 'Cadastros no dia' } },
                },
            },
        };
    },
    computed: {
        mediaColaboradores() {
            return this.media.labels || [];
        },
        colaboradores() {
            return this.evolucao.datasets.map((d) => d.label);
        },
        // primeiro e segundo nome; se dois colaboradores colidirem, mantém o nome completo
        nomeExibicao() {
            const todos = [...new Set([...this.mediaColaboradores, ...this.colaboradores])];
            const usos = todos.reduce((acc, nome) => {
                const curto = nomeCurto(nome);
                acc[curto] = (acc[curto] || 0) + 1;
                return acc;
            }, {});
            return Object.fromEntries(todos.map((nome) => [nome, usos[nomeCurto(nome)] > 1 ? nome : nomeCurto(nome)]));
        },
        mediaColaboradoresOpcoes() {
            return this.mediaColaboradores.map((nome) => ({ value: nome, label: this.nomeExibicao[nome] }));
        },
        colaboradoresOpcoes() {
            return this.colaboradores.map((nome) => ({ value: nome, label: this.nomeExibicao[nome] }));
        },
        top5Colaboradores() {
            return [...this.evolucao.datasets]
                .map((d) => ({
                    label: d.label,
                    soma: (d.data || []).reduce((acc, v) => acc + (Number(v) || 0), 0),
                }))
                .sort((a, b) => b.soma - a.soma)
                .slice(0, 5)
                .map((item) => item.label);
        },
        mediaChart() {
            const labelsOriginais = this.media.labels || [];
            const valoresOriginais = this.media.data || [];
            const selecionados = new Set(this.mediaColaboradoresVisiveis);
            const labels = [];
            const data = [];

            labelsOriginais.forEach((nome, idx) => {
                if (selecionados.has(nome)) {
                    labels.push(this.nomeExibicao[nome]);
                    data.push(valoresOriginais[idx]);
                }
            });

            return {
                labels,
                datasets: [{ label: 'Média diária', data, backgroundColor: '#3B82F6' }],
            };
        },
        evolucaoChart() {
            return {
                labels: (this.evolucao.labels || []).map(rotuloDia),
                datasets: (this.evolucao.datasets || [])
                    .map((d, i) => ({ ...d, borderColor: CORES[i % CORES.length], backgroundColor: CORES[i % CORES.length], tension: 0.3 }))
                    .filter((d) => this.colaboradoresVisiveis.includes(d.label))
                    .map((d) => ({ ...d, label: this.nomeExibicao[d.label] })),
            };
        },
    },
    watch: {
        query: {
            immediate: true,
            handler(novaQuery) {
                if (novaQuery) this.load();
            },
        },
    },
    methods: {
        selecionarMediaPadrao() {
            this.mediaColaboradoresVisiveis = this.mediaColaboradores.slice(0, MAX_MEDIA_BARS);
        },
        selecionarMediaTop5() {
            this.mediaColaboradoresVisiveis = this.mediaColaboradores.slice(0, 5);
        },
        selecionarMediaTodos() {
            this.mediaColaboradoresVisiveis = [...this.mediaColaboradores];
        },
        limparSelecaoMedia() {
            this.mediaColaboradoresVisiveis = [];
        },
        selecionarTop5() {
            this.colaboradoresVisiveis = [...this.top5Colaboradores];
        },
        selecionarTodos() {
            this.colaboradoresVisiveis = [...this.colaboradores];
        },
        limparSelecao() {
            this.colaboradoresVisiveis = [];
        },
        async load() {
            if (!this.query) return;
            const reqId = ++this.reqId;
            this.loading = true;
            try {
                const [media, evolucao] = await Promise.all([
                    Conta.dashboardMediaDiaria(this.query),
                    Conta.dashboardEvolucaoDiaria(this.query),
                ]);
                if (reqId !== this.reqId) return;
                this.media = media.data || { labels: [], data: [] };
                const dados = evolucao.data || { labels: [], datasets: [] };
                const soma = (d) => (d.data || []).reduce((acc, v) => acc + (Number(v) || 0), 0);
                this.evolucao = { ...dados, datasets: [...(dados.datasets || [])].sort((x, y) => soma(y) - soma(x)) };
                this.selecionarMediaPadrao();
                this.selecionarTop5();
            } catch (error) {
                if (reqId !== this.reqId) return;
                console.error('Erro ao carregar gráficos de cadastro de boleto:', error);
                this.$toast.add({
                    severity: 'error',
                    summary: 'Erro',
                    detail: error.response?.data?.msg || 'Falha ao carregar gráficos de cadastro de boleto',
                    life: 5000,
                });
            } finally {
                if (reqId === this.reqId) this.loading = false;
            }
        },
    },
};
</script>

<style scoped>
.chart-card {
    min-width: 0;
}
.chart-area {
    position: relative;
    min-height: 280px;
}
.colaborador-multiselect {
    height: 2.35rem;
    align-items: center;
}
.chart-loader {
    position: absolute;
    inset: 0;
    background: rgba(26, 26, 26, 0.7);
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    z-index: 10;
}
:global(html:not(.my-app-dark) .chart-loader) {
    background: rgba(255, 255, 255, 0.7);
}
.chart-loader span {
    margin-top: 12px;
    font-size: 0.85rem;
    font-weight: 700;
    color: #f97316;
    letter-spacing: 0.05em;
}
</style>
